import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useMutation } from '../lib/use-api';
import { ErrorNotice } from '../components/Feedback';
import { useNavigate } from 'react-router-dom';

/**
 * Giao diện nhập liệu kịch bản và sinh bài giảng SCORM bằng AI.
 */
export function AiBuilderPage(): ReactNode {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [provider, setProvider] = useState('gemini');
  const [model, setModel] = useState('gemini-3.5-flash-lite');
  const [apiKey, setApiKey] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Cập nhật model mặc định khi đổi nền tảng
  useEffect(() => {
    if (provider === 'gemini') setModel('gemini-3.5-flash-lite');
    else if (provider === 'openai') setModel('gpt-4o-mini');
    else if (provider === 'anthropic') setModel('claude-3-5-sonnet-20240620');
  }, [provider]);

  // Hook gọi API
  const { run, pending, error } = useMutation<
    { text: string; provider: string; model: string; apiKey: string },
    { contentId: string }
  >(async (payload) => {
    return apiFetch<{ contentId: string }>('/api/admin/ai/build-h5p', {
      method: 'POST',
      body: payload,
    });
  });

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setIsSuccess(false);
    
    const result = await run({ text, provider, model, apiKey });
    if (result) {
      setIsSuccess(true);
      // Chuyển hướng tới trang danh sách học liệu và mở xem thử
      navigate('/content', { state: { previewContentId: result.contentId } });
    }
  };

  return (
    <section>
      <h1>AI Builder</h1>
      <p className="hint" style={{ marginBottom: '1.5rem', maxWidth: '800px', lineHeight: 1.5 }}>
        Dán kịch bản, giáo án hoặc các tình huống thực hành vào đây. Hệ thống sẽ tự động sử dụng AI
        phân tích, trích xuất và chuyển thể thành một bài giảng tương tác (H5P Course Presentation).
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="field">
          <span>Nền tảng AI</span>
          <select value={provider} onChange={(e) => setProvider(e.target.value)} disabled={pending}>
            <option value="gemini">Google Gemini</option>
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic</option>
          </select>
        </div>
        <div className="field">
          <span>Model</span>
          <input 
            type="text" 
            value={model} 
            onChange={(e) => setModel(e.target.value)} 
            disabled={pending}
            style={{ fontFamily: 'monospace' }}
          />
        </div>
        <div className="field">
          <span>API Key</span>
          <input 
            type="password" 
            placeholder="Nhập API Key..." 
            value={apiKey} 
            onChange={(e) => setApiKey(e.target.value)} 
            disabled={pending}
            style={{ fontFamily: 'monospace' }}
          />
        </div>
      </div>

      <div className="field">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <span>Nội dung kịch bản đầu vào</span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              className="button--secondary"
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
              onClick={() => setText(prev => prev + (prev.endsWith('\n') || !prev ? '' : '\n\n') + '### CHỦ ĐỀ VÀ MỤC TIÊU:\n- Chủ đề: \n- Mục tiêu: \n')}
              disabled={pending}
            >
              + Tiêu đề & Mục tiêu
            </button>
            <button
              type="button"
              className="button--secondary"
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
              onClick={() => setText(prev => prev + (prev.endsWith('\n') || !prev ? '' : '\n\n') + '### NỘI DUNG LÝ THUYẾT:\n')}
              disabled={pending}
            >
              + Lý thuyết
            </button>
            <button
              type="button"
              className="button--secondary"
              style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
              onClick={() => setText(prev => prev + (prev.endsWith('\n') || !prev ? '' : '\n\n') + '### TÌNH HUỐNG/CÂU HỎI:\n')}
              disabled={pending}
            >
              + Tình huống
            </button>
          </div>
        </div>
        <textarea
          style={{ width: '100%', minHeight: '400px', resize: 'vertical', fontFamily: 'monospace' }}
          placeholder="Dán nội dung giáo án vào đây, sử dụng các nút bên trên để đánh dấu các phần quan trọng..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={pending}
        />
      </div>

      {error && (
        <ErrorNotice error={error} />
      )}
      
      {isSuccess && (
        <p style={{ color: 'var(--color-accent)', fontWeight: 500, marginTop: '1rem' }}>
          Tạo bài giảng thành công! Đang chuyển hướng...
        </p>
      )}

      <div className="toolbar" style={{ marginTop: '1.5rem', padding: 0, border: 'none', background: 'transparent', boxShadow: 'none' }}>
        <button
          onClick={handleSubmit}
          disabled={pending || !text.trim()}
          className="button--primary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
        >
          {pending ? (
            'Đang xử lý (Khoảng 10-30s)...'
          ) : (
            <>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Sinh bài giảng tương tác</span>
            </>
          )}
        </button>

        <button
          onClick={() => setText('')}
          disabled={pending || !text}
        >
          Xoá trắng
        </button>
      </div>
    </section>
  );
}
